import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Button, Card, Input, Alert, Loader } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

const BLANK = { label: '', key: '', description: '', is_active: true, probe: '', cs_fields: '[]' };

function HeaderActions({ isNew, row, saving, setField, save, remove }) {
  return (
    <Can module="connections" action="author">
      <div className="flex items-center gap-2">
        {!isNew && (
          <>
            <label className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={!!row.is_active} onChange={(e) => setField('is_active', e.target.checked)} /> Active
            </label>
            <Button size="sm" variant="secondary" onClick={remove}><Trash2 size={14} className="mr-1 inline" /> Delete</Button>
          </>
        )}
        <Button size="sm" onClick={save} disabled={saving || !row.label || !row.key}>{saving ? 'Saving…' : 'Save'}</Button>
      </div>
    </Can>
  );
}

export default function ConnectionProviderDetail() {
  const { id } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const { fetchApi } = useAppApi();

  const [row, setRow] = useState(isNew ? { ...BLANK } : null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    fetchApi('crud', { model: 'AppConnectionProvider', action: 'show', where: { id } })
      .then((res) => setRow(res.data || res))
      .catch((ex) => setErr(apiError(ex)))
      .finally(() => setLoading(false));
  }, [id]);

  const setField = (key, value) => setRow((r) => ({ ...r, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      if (isNew) {
        const res = await fetchApi('crud', { model: 'AppConnectionProvider', action: 'create', data: row });
        nav(`/app-connection-provider/${res.data.id || res.id}`, { replace: true });
      } else {
        await fetchApi('crud', { model: 'AppConnectionProvider', action: 'update', where: { id }, data: row });
      }
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete provider "${row.label}"? This cannot be undone.`)) return;
    try {
      await fetchApi('crud', { model: 'AppConnectionProvider', action: 'delete', where: { id } });
      nav('/app-connection-provider');
    } catch (ex) {
      setErr(apiError(ex));
    }
  };

  if (loading) return <PageShell><Loader text="Loading provider…" /></PageShell>;
  if (!row) return <PageShell><Alert variant="error" title="Could not load">{err || 'Provider not found'}</Alert></PageShell>;

  return (
    <PageShell width="narrow" className="space-y-3">
      <PageHeader
        backTo="/app-connection-provider"
        backLabel="Connection Providers"
        title={isNew ? 'New Provider' : row.label}
        actions={(
          <HeaderActions isNew={isNew} row={row} saving={saving} setField={setField} save={save} remove={remove} />
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}

      <Card className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Label" value={row.label || ''} onChange={(e) => setField('label', e.target.value)} />
          <Input label="Key" value={row.key || ''} disabled={!isNew} onChange={(e) => setField('key', e.target.value)} />
          <Input label="Description" value={row.description || ''} onChange={(e) => setField('description', e.target.value)} />
          <Input label="Probe" value={row.probe || ''} onChange={(e) => setField('probe', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm mb-1">cs_fields (JSON)</label>
          <textarea
            className="w-full min-h-[120px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm font-mono"
            value={row.cs_fields || '[]'}
            onChange={(e) => setField('cs_fields', e.target.value)}
          />
        </div>
      </Card>
    </PageShell>
  );
}
